import { ReactNode, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box, ButtonBase, Link, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import './ErrorPage.css';
import { ErrorSidebar } from '@/components/Common/Error/ErrorSidebar';
import { I18N_KEYS } from '@/language/keys';
import { ChatCircleIcon } from '@/UI/Icons/ChatCircleIcon';
import { MonitorIcon } from '@/UI/Icons/MonitorIcon';
import { RefreshIcon } from '@/UI/Icons/RefreshIcon';
import { RestartIcon } from '@/UI/Icons/RestartIcon';
import { WarningCircleIcon } from '@/UI/Icons/WarningCircleIcon';
import { openIntercom } from '@/providers/intercom';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { color } from '@/colors';
import { FlexColCenHor } from '@/UI/styles';
import { TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';

const K = I18N_KEYS.GPU_DRIVER_ERROR_PAGE;

export const GPUDriverErrorPage = () => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  useEffect(() => {
    track('gpu_driver_error_page_viewed', {}, TrackTypeEnum.BI);
  }, [track]);

  return (
    <div className="error-page">
      <ErrorSidebar />
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: 1, height: 1 }}>
        <FlexColCenHor
          sx={{
            pt: '36px',
            pb: 3,
            px: 3,
            background: `linear-gradient(${color.Weavy_Error_08_T}, ${color.Weavy_Error_08_T}), ${color.Black100}`,
            border: `1px solid ${color.Weavy_Error_64_T}`,
            borderRadius: '8px',
            width: 1,
            maxWidth: 440,
            gap: 3,
          }}
        >
          <FlexColCenHor sx={{ gap: 1 }}>
            <WarningCircleIcon style={{ width: 24, height: 24, color: color.Weavy_Error }} />
            <Typography variant="body-sm-md" color={color.Weavy_Error}>
              {t(K.TITLE)}
            </Typography>
          </FlexColCenHor>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', width: 1 }}>
            <Typography variant="body-sm-rg" color={color.White80_T}>
              {t(K.DESCRIPTION)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: 1 }}>
              <StepCard icon={<RefreshIcon width={16} height={16} />} title={t(K.STEP_REFRESH_TITLE)}>
                {t(K.STEP_REFRESH_DESCRIPTION)}
              </StepCard>
              <StepCard icon={<MonitorIcon width={16} height={16} />} title={t(K.STEP_HARDWARE_ACCELERATION_TITLE)}>
                <Trans
                  i18nKey={K.STEP_HARDWARE_ACCELERATION_DESCRIPTION}
                  components={{
                    learnHow: (
                      <Link
                        href={EXTERNAL_LINKS.browserConfiguration}
                        target="_blank"
                        rel="noreferrer"
                        underline="always"
                        sx={{ color: color.White40_T, textDecorationColor: 'inherit' }}
                        onClick={() => track('gpu_driver_error_learn_how_clicked', {}, TrackTypeEnum.BI)}
                      />
                    ),
                  }}
                />
              </StepCard>
              <StepCard icon={<RestartIcon />} title={t(K.STEP_RESTART_TITLE)}>
                {t(K.STEP_RESTART_DESCRIPTION)}
              </StepCard>
              <StepCard icon={<ChatCircleIcon width={16} height={16} />} title={t(K.STEP_CONTACT_SUPPORT_TITLE)}>
                <Trans
                  i18nKey={K.STEP_CONTACT_SUPPORT_DESCRIPTION}
                  components={{
                    supportEmail: (
                      <ButtonBase
                        onClick={openIntercom}
                        disableRipple
                        sx={{
                          color: color.White40_T,
                          textDecoration: 'underline',
                          font: 'inherit',
                          verticalAlign: 'baseline',
                        }}
                      />
                    ),
                  }}
                />
              </StepCard>
            </Box>
          </Box>
        </FlexColCenHor>
      </Box>
    </div>
  );
};

const StepCardRoot = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: 16,
  backgroundColor: color.White04_T,
  borderRadius: 8,
  textAlign: 'left',
});

function StepCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <StepCardRoot>
      <Box sx={{ color: color.White64_T, flexShrink: 0, width: 16, height: 16, mt: '1px' }}>{icon}</Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <TextSmMd color={color.White100}>{title}</TextSmMd>
        <TextSmRg color={color.White40_T}>{children}</TextSmRg>
      </Box>
    </StepCardRoot>
  );
}
